<template>
  <div class="overflow-x-hidden bg-neutral-50">
    <Head :title="$t('articles.title')" />

    <div class="mx-auto w-full min-w-0 max-w-[1200px] px-4 py-8 sm:px-6 lg:px-0">
      <!-- Breadcrumb -->
      <nav class="mb-6 flex min-w-0 flex-wrap items-center gap-1 text-sm text-neutral-700" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="text-[#2ea8cf]">{{ $t('breadcrumbs.articles') }}</span>
      </nav>

      <!-- Page title -->
      <h1 class="text-[32px] font-medium leading-10 tracking-[-0.02em] lg:text-[40px] lg:leading-[48px]">
        {{ $t('articles.title') }}
      </h1>

      <!-- Site filters (global listing only — per-site pages already scope articles) -->
      <div v-if="showSiteFilters" class="mt-6">
        <div
          class="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden lg:relative lg:left-auto lg:w-full lg:max-w-none lg:translate-x-0 lg:overflow-visible"
        >
          <div class="flex min-w-full w-max flex-nowrap items-center gap-2 pb-1 pl-4 pr-4 lg:min-w-0 lg:w-full lg:flex-wrap lg:pb-0 lg:pl-0 lg:pr-0">
            <button
              v-for="filter in siteFilters"
              :key="filter.value"
              type="button"
              :class="siteFilterButtonClass(filter.value)"
              @click="activeSiteFilter = filter.value"
            >
              {{ filter.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Cards grid -->
      <div v-if="paginatedArticles.length > 0" class="mt-8 lg:mt-10">
        <div class="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
          <Link
            v-for="article in paginatedArticles"
            :key="article.id"
            :href="articleHref(article)"
            class="group flex min-w-0 flex-col overflow-hidden rounded-xl no-underline"
          >
            <div class="aspect-[16/10] w-full shrink-0 overflow-hidden rounded-xl bg-neutral-200 md:h-[216px] md:aspect-auto">
              <img
                v-if="article.cover_image"
                :src="article.cover_image"
                :alt="article.title"
                class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              >
              <div
                v-else
                class="flex h-full w-full items-center justify-center bg-neutral-100 text-neutral-400"
              >
                <svg class="h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            <div class="flex min-w-0 flex-col gap-2 py-4 lg:py-6">
              <NeutralPill
                v-if="article.site_context_label"
                :label="article.site_context_label"
              />
              <div class="flex min-w-0 flex-wrap items-center gap-2">
                <h2 class="min-w-0 break-words text-xl font-semibold leading-7 tracking-[-0.02em] text-neutral-900 group-hover:text-[#2ea8cf]">
                  {{ article.title }}
                </h2>
                <span
                  v-if="article.status === 'draft'"
                  class="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"
                >
                  {{ $t('articles.draft') }}
                </span>
              </div>
              <p v-if="article.excerpt" class="line-clamp-3 break-words text-base leading-6 text-neutral-500">
                {{ article.excerpt }}
              </p>
              <p v-if="article.published_at" class="text-sm leading-5 text-neutral-500">
                {{ formatDate(article.published_at) }}
              </p>
            </div>
          </Link>
        </div>

        <!-- Pagination -->
        <FrontofficeListPagination
          v-model:current-page="currentPage"
          :total-pages="totalPages"
          :aria-label="$t('articles.pagination')"
        />
      </div>

      <div v-else class="py-16 text-center">
        <h3 class="text-sm font-semibold text-neutral-900">{{ $t('articles.empty_title') }}</h3>
        <p class="mt-1 text-sm text-neutral-500">
          {{ $t('articles.empty_hint') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import NeutralPill from '@/Shared/NeutralPill.vue';
import FrontofficeListPagination from '@/Shared/FrontofficeListPagination.vue';
import { useClientPagination } from '@/utils/useClientPagination';

const PER_PAGE = 9;

const SITE_FILTER_OPTIONS = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'code_medical_center', label: 'Centrum Medyczne CODE' },
  { value: 'infertility_treatment_clinic', label: 'Klinika Przyczynowego Leczenia Niepłodności' },
  { value: 'family_clinic', label: 'Klinika Zdrowia Rodziny' },
  { value: 'institute', label: 'Instytut Genomiki i Medycyny Molekularnej' },
];

export default {
  name: 'ArticlesIndex',
  components: { Head, Link, NeutralPill, FrontofficeListPagination },
  props: {
    articles: { type: Array, default: () => [] },
    // eslint-disable-next-line vue/prop-name-casing
    site_context: { type: String, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_label: { type: String, default: '' },
  },
  setup(props) {
    const activeSiteFilter = ref('all');

    const showSiteFilters = computed(() => (
      !props.site_context || props.site_context === 'code_medical_center'
    ));

    const filteredArticles = computed(() => {
      if (!showSiteFilters.value || activeSiteFilter.value === 'all') {
        return props.articles;
      }
      return props.articles.filter((a) => a.site_context === activeSiteFilter.value);
    });

    const {
      currentPage,
      totalPages,
      paginatedItems: paginatedArticles,
    } = useClientPagination(filteredArticles, PER_PAGE, {
      resetWatchSources: [activeSiteFilter],
    });

    return {
      activeSiteFilter,
      showSiteFilters,
      filteredArticles,
      currentPage,
      totalPages,
      paginatedArticles,
    };
  },
  computed: {
    siteFilters() {
      return SITE_FILTER_OPTIONS;
    },
    homeHref() {
      const ctx = this.site_context;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[ctx]) {
        return this.$routes[ctx]();
      }
      return '/';
    },
  },
  methods: {
    siteFilterButtonClass(value) {
      return [
        'shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors lg:shrink lg:whitespace-normal',
        this.activeSiteFilter === value
          ? 'bg-neutral-900 text-white'
          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
      ];
    },
    articleHref(article) {
      // Articles are served only from the global, context-free path.
      return this.$routes.article(article.slug);
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleDateString('pl-PL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch {
        return iso;
      }
    },
  },
};
</script>
